import actionNames from './actionNames'
import logger from '@/plugins/logger.js'

const actions = {
  async getData({ dispatch }, reportKey) {
    return dispatch('tools/setView', reportKey, { root: true })
  },
  // Navigation actions
  showSettingsView({ commit }, view) {
    commit(actionNames.SHOW_SETTINGS_VIEW, view)
  },

  // User Settings actions
  async fetchUserSettings({ commit, rootGetters }) {
    commit(actionNames.GET_USER_SETTINGS_REQUESTED)

    try {
      const currentUser = rootGetters['auth/currentUser']

      if (!currentUser) {
        throw new Error('No user found')
      }

      // Extract user settings from the user object
      const userSettings = {
        profile: {
          firstName: currentUser.personNameFirst || '',
          lastName: currentUser.personNameLast || '',
          email: currentUser.email || currentUser.personEmail || '',
          phone: currentUser.phone || '',
          title: currentUser.title || '',
          department: currentUser.department || '',
        },
        preferences: currentUser.settings?.preferences || {},
        notifications: currentUser.settings?.notifications || {},
        security: currentUser.settings?.security || {},
        privacy: currentUser.settings?.privacy || {},
      }

      commit(actionNames.GET_USER_SETTINGS_SUCCEEDED, userSettings)

      return userSettings
    } catch (error) {
      commit(actionNames.GET_USER_SETTINGS_FAILED, error)

      throw error
    }
  },

  async updateUserSettings({ commit, dispatch, rootGetters }, { section, settings }) {
    commit(actionNames.UPDATE_USER_SETTINGS_REQUESTED)

    try {
      const currentUser = rootGetters['auth/currentUser']

      if (!currentUser) {
        throw new Error('No user found')
      }

      // Update the specific section of settings
      const updatedSettings = { [section]: settings }

      // Prepare the document for the auth store
      // The auth store expects { doc, docId } format
      // We need to clean the user object of Firebase-specific metadata
      const cleanUser = await dispatch('cleanFirebaseMetadata', currentUser)

      const userDoc = {
        ...cleanUser,
        settings: {
          ...cleanUser.settings,
          [section]: settings,
        },
      }

      // Update the user document in the auth store
      await dispatch('auth/updateUserSettings', {
        doc: userDoc,
        docId: currentUser.email || currentUser.docId,
      }, { root: true })

      commit(actionNames.UPDATE_USER_SETTINGS_SUCCEEDED, updatedSettings)
      return updatedSettings
    } catch (error) {
      commit(actionNames.UPDATE_USER_SETTINGS_FAILED, error)
      throw error
    }
  },

  // Profile actions
  async updateProfile({ dispatch }, profileData) {
    return dispatch('updateUserSettings', {
      section: 'profile',
      settings: profileData,
    })
  },

  // Preferences actions
  async updatePreferences({ dispatch }, preferencesData) {
    return dispatch('updateUserSettings', {
      section: 'preferences',
      settings: preferencesData,
    })
  },

  async updateTheme({ dispatch, commit }, theme) {
    commit('updateTheme', theme)

    // Update layout store for immediate UI changes
    await dispatch('layout/setDarkMode', theme === 'dark', { root: true })

    // Update user settings in auth store
    await dispatch('updatePreferences', { theme })

    return theme
  },

  // Notifications actions
  async updateNotifications({ dispatch }, notificationsData) {
    return dispatch('updateUserSettings', {
      section: 'notifications',
      settings: notificationsData,
    })
  },

  // Security actions
  async updateSecurity({ dispatch }, securityData) {
    return dispatch('updateUserSettings', {
      section: 'security',
      settings: securityData,
    })
  },

  async enableTwoFactorAuth({ dispatch }) {
    return dispatch('updateSecurity', { twoFactorAuth: true })
  },

  async disableTwoFactorAuth({ dispatch }) {
    return dispatch('updateSecurity', { twoFactorAuth: false })
  },

  // Privacy actions
  async updatePrivacy({ dispatch }, privacyData) {
    return dispatch('updateUserSettings', {
      section: 'privacy',
      settings: privacyData,
    })
  },

  // Demo mode actions
  async updateDemoMode({ commit, dispatch }, demoSettings) {
    commit('updateDemoMode', demoSettings)

    // Update auth store if needed
    if (demoSettings.enabled !== undefined) {
      await dispatch('auth/updateDemoMode', demoSettings.enabled, { root: true })
    }

    return demoSettings
  },

  // Alias actions
  async updateAliasSettings({ commit, dispatch }, aliasSettings) {
    commit('updateAliasSettings', aliasSettings)

    // Update auth store
    await dispatch('auth/updateAliasMode', aliasSettings, { root: true })

    return aliasSettings
  },

  // Filters and reports actions
  async updateSavedFilters({ commit, rootGetters }) {
    const filters = rootGetters['auth/filters'] || []
    commit('updateSavedFilters', filters)
    return filters
  },

  async updateCustomReports({ commit, rootGetters }) {
    const reports = rootGetters['auth/customReports'] || []
    commit('updateCustomReports', reports)
    return reports
  },

  // Reset actions
  async resetSettings({ commit, dispatch }) {
    commit(actionNames.RESET_SETTINGS_REQUESTED)

    try {
      commit('resetSettings')

      // Reload settings from auth store
      await dispatch('fetchUserSettings')

      return true
    } catch (error) {
      commit(actionNames.RESET_SETTINGS_FAILED, error)
      throw error
    }
  },

  // Export/Import actions
  async exportSettings({ commit, getters }) {
    commit(actionNames.EXPORT_SETTINGS_REQUESTED)

    try {
      const exportData = getters.exportableSettings

      // Create downloadable file
      const dataStr = JSON.stringify(exportData, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      const url = URL.createObjectURL(dataBlob)

      const link = document.createElement('a')
      link.href = url
      link.download = `settings-backup-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      commit(actionNames.EXPORT_SETTINGS_SUCCEEDED)
      return exportData
    } catch (error) {
      commit(actionNames.EXPORT_SETTINGS_FAILED, error)
      throw error
    }
  },

  async importSettings({ commit, dispatch }, importData) {
    commit(actionNames.IMPORT_SETTINGS_REQUESTED)

    try {
      // Validate import data
      if (!importData || typeof importData !== 'object') {
        throw new Error('Invalid settings data')
      }

      // Import each section if present
      if (importData.preferences) {
        await dispatch('updatePreferences', importData.preferences)
      }

      if (importData.notifications) {
        await dispatch('updateNotifications', importData.notifications)
      }

      if (importData.security) {
        await dispatch('updateSecurity', importData.security)
      }

      if (importData.privacy) {
        await dispatch('updatePrivacy', importData.privacy)
      }

      if (importData.savedFilters) {
        commit('updateSavedFilters', importData.savedFilters)
      }

      if (importData.customReports) {
        commit('updateCustomReports', importData.customReports)
      }

      commit(actionNames.IMPORT_SETTINGS_SUCCEEDED)
      return true
    } catch (error) {
      commit(actionNames.IMPORT_SETTINGS_FAILED, error)
      throw error
    }
  },

  // Utility actions
  clearError({ commit }) {
    commit('clearError')
  },

  // Clean Firebase-specific metadata that cannot be serialized
  cleanFirebaseMetadata(context, user) {
    if (!user || typeof user !== 'object') {
      return user
    }

    // Create a clean copy without Firebase-specific fields
    const cleanUser = { ...user }

    // Remove Firebase Auth specific fields that contain non-serializable data
    const firebaseFields = [
      'metadata',
      'multiFactor',
      'providerData',
      'refreshToken',
      'accessToken',
      'auth',
      'stsTokenManager',
      '_delegate',
    ]

    firebaseFields.forEach((field) => {
      delete cleanUser[field]
    })

    return cleanUser
  },

  // Initialize settings on app load
  async initializeSettings({ dispatch }) {
    try {
      await dispatch('fetchUserSettings')
      await dispatch('updateSavedFilters')
      await dispatch('updateCustomReports')
    } catch (error) {
      logger.console.error('Failed to initialize settings:', error)
    }
  },
}

export default actions
